import React, { useState, useEffect } from 'react';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Utility function to generate a unique variant ID if a dedicated ID is missing
const generateVariantId = (variant, size, soleType) => {
    // If variant has an explicit ID (best practice), use it combined with size and sole type
    if (variant.id) {
        return `${variant.id}-${size}-${soleType}`;
    }
    // Fallback: create a unique identifier from core properties
    const color = variant.PSoleColor || variant.PTypeName || 'Default';
    return `${color.replace(/\s/g, '_')}-${size}-${soleType}`;
};

// Sole types configuration (must match ProductDetail)
const soleTypes = [
    { name: 'Leather', priceAdjustment: 0 },
    { name: 'Vibrum', priceAdjustment: 0 },
    { name: 'Rubber', priceAdjustment: -1000 }
];

// Calculate price with sole type adjustment
const calculateAdjustedPrice = (basePrice, soleType) => {
    const selectedSole = soleTypes.find(sole => sole.name === soleType);
    const adjustment = selectedSole ? selectedSole.priceAdjustment : 0;
    return Math.max(0, basePrice + adjustment);
};

// Add to cart function (exported for use in other components)
export const addToCart = (productData) => {
    console.log('addToCart called with:', productData);
    
    const { productId, variant, size, quantity, product, soleType = 'Leather' } = productData;
    
    // --- FIX: Use a consistently generated unique variant ID with sole type ---
    const uniqueVariantKey = generateVariantId(variant, size, soleType);

    // Calculate base prices
    const basePrice = variant.PPrice || 0;
    const baseDiscountPrice = variant.PInOffer ? (variant.PDiscountPrice || 0) : null;
    
    // Apply sole type adjustments
    const adjustedPrice = calculateAdjustedPrice(basePrice, soleType);
    const adjustedDiscountPrice = baseDiscountPrice ? calculateAdjustedPrice(baseDiscountPrice, soleType) : null;

    const cartItem = {
        // Core identifiers
        productID: productId,
        productVariantKey: uniqueVariantKey, // Use this for matching
        productSize: size,
        productSoleType: soleType, // Store sole type

        // Display and pricing info
        productQuantity: quantity,
        productName: product.name || product.PName,
        productPrice: adjustedPrice, // Store adjusted price
        productDiscountPrice: adjustedDiscountPrice, // Store adjusted discount price
        productBasePrice: basePrice, // Store original base price for reference
        productBaseDiscountPrice: baseDiscountPrice, // Store original discount price for reference
        productImage: variant.PImgUrls?.[0] || '',
        productColor: variant.PSoleColor || variant.PTypeName || 'Default',
        soleAdjustment: soleTypes.find(sole => sole.name === soleType)?.priceAdjustment || 0
    };

    console.log('Cart item created:', cartItem);

    const existingCart = JSON.parse(localStorage.getItem('shoppingCart') || '[]');
    console.log('Existing cart before update:', existingCart);
    
    // Check if item already exists in cart based on all unique keys including sole type
    const existingItemIndex = existingCart.findIndex(item => 
        item.productID === cartItem.productID && 
        item.productVariantKey === cartItem.productVariantKey
    );

    console.log('Existing item index:', existingItemIndex);

    if (existingItemIndex > -1) {
        // Update quantity if item exists
        existingCart[existingItemIndex].productQuantity += quantity;
        console.log('Updated existing item quantity');
    } else {
        // Add new item
        existingCart.push(cartItem);
        console.log('Added new item to cart');
    }

    localStorage.setItem('shoppingCart', JSON.stringify(existingCart));
    console.log('Cart saved to localStorage, total items:', existingCart.length);
    
    // Dispatch event to notify cart updates (Crucial for the ProductDetail page)
    window.dispatchEvent(new Event('cartUpdated'));
    console.log('cartUpdated event dispatched');
    
    return existingCart;
};

const Cart = () => {
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Load cart from localStorage on component mount and listen for updates
    const loadCart = () => {
        console.log('Loading cart from localStorage...');
        const savedCart = localStorage.getItem('shoppingCart');
        
        if (savedCart) {
            try {
                const parsedCart = JSON.parse(savedCart);
                console.log('Parsed cart successfully.');
                setCartItems(parsedCart);
            } catch (error) {
                console.error('Error loading/parsing cart:', error);
                setCartItems([]);
            }
        } else {
            console.log('No cart data found.');
            setCartItems([]);
        }
        setLoading(false);
    };

    // 1. Initial Load and Event Listener Setup
    useEffect(() => {
        loadCart();

        const handleCartUpdate = () => {
            console.log('Cart update event received, reloading cart...');
            loadCart();
        };

        window.addEventListener('cartUpdated', handleCartUpdate);

        return () => {
            window.removeEventListener('cartUpdated', handleCartUpdate);
        };
    }, []); // Only runs on mount and unmount

    // 2. Save cart to localStorage whenever cartItems state changes
    useEffect(() => {
        // Skip saving on initial load when items might still be [].
        // The loadCart function handles initial loading.
        if (!loading) { 
            console.log('Cart state updated, saving to localStorage.');
            localStorage.setItem('shoppingCart', JSON.stringify(cartItems));
            // Note: No need to dispatch 'cartUpdated' here, 
            // as this useEffect is triggered *after* an action 
            // (like removing or updating quantity) that only affects this component.
        }
    }, [cartItems, loading]);

    // Remove item from cart
    const removeFromCart = (index) => {
        console.log('Removing item at index:', index);
        const updatedCart = cartItems.filter((_, i) => i !== index);
        setCartItems(updatedCart);
    };

    // Update item quantity
    const updateQuantity = (index, newQuantity) => {
        if (newQuantity < 1) return;
        
        console.log('Updating quantity for index:', index, 'to:', newQuantity);
        const updatedCart = cartItems.map((item, i) => 
            i === index ? { ...item, productQuantity: newQuantity } : item
        );
        setCartItems(updatedCart);
    };

    // Update sole type for an item
    const updateSoleType = (index, newSoleType) => {
        console.log('Updating sole type for index:', index, 'to:', newSoleType);
        
        const updatedCart = cartItems.map((item, i) => {
            if (i === index) {
                const selectedSole = soleTypes.find(sole => sole.name === newSoleType);
                const adjustment = selectedSole ? selectedSole.priceAdjustment : 0;
                
                // Recalculate prices with new sole type
                const newPrice = Math.max(0, item.productBasePrice + adjustment);
                const newDiscountPrice = item.productBaseDiscountPrice ? 
                    Math.max(0, item.productBaseDiscountPrice + adjustment) : null;

                // Generate new variant key with updated sole type
                const newVariantKey = generateVariantId(
                    { 
                        id: item.productID,
                        PSoleColor: item.productColor,
                        PTypeName: item.productColor 
                    }, 
                    item.productSize, 
                    newSoleType
                );

                return {
                    ...item,
                    productSoleType: newSoleType,
                    productPrice: newPrice,
                    productDiscountPrice: newDiscountPrice,
                    productVariantKey: newVariantKey,
                    soleAdjustment: adjustment
                };
            }
            return item;
        });
        
        setCartItems(updatedCart);
    };

    // Calculate total price (Subtotal)
    const calculateTotal = () => {
        const total = cartItems.reduce((total, item) => {
            const price = item.productDiscountPrice || item.productPrice;
            return total + (price * item.productQuantity);
        }, 0);
        return total;
    };

    // Calculate total items count
    const calculateTotalItems = () => {
        const totalItems = cartItems.reduce((total, item) => total + item.productQuantity, 0);
        return totalItems;
    };

    // Continue shopping
    const continueShopping = () => {
        navigate('/shop');
    };

    // Proceed to checkout
    const proceedToCheckout = () => {
        navigate('/checkout');
    };

    // Debug function to check localStorage
    const debugCart = () => {
        const cart = localStorage.getItem('shoppingCart');
        console.log('=== DEBUG CART ===');
        console.log('LocalStorage shoppingCart:', cart);
        console.log('Parsed cart:', JSON.parse(cart || '[]'));
        console.log('Component state cartItems:', cartItems);
        console.log('==================');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-beige p-6">
                <div className="max-w-4xl mx-auto">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
                        <div className="space-y-4">
                            {[1, 2].map(i => (
                                <div key={i} className="h-32 bg-gray-300 rounded"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (cartItems.length === 0) {
        return (
            <div className="min-h-screen bg-beige p-6">
                <div className="m-4">" "</div>
                <div className="max-w-4xl mx-auto text-center">
                    <div className="bg-offwhite rounded-lg p-12 shadow-sm">
                        <ShoppingBag size={64} className="mx-auto text-charcoal/30 mb-4" />
                        <h1 className="text-3xl font-bold text-charcoal mb-4">Your Cart is Empty</h1>
                        <p className="text-charcoal/70 mb-8 max-w-md mx-auto">
                            Looks like you haven't added any items to your cart yet. Start shopping to find amazing products!
                        </p>
                        <button
                            onClick={continueShopping}
                            className="bg-primary text-offwhite px-8 py-3 rounded-lg hover:bg-primary-dark transition-colors font-semibold"
                        >
                            Start Shopping
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-beige p-6">
            {/* Debug button - remove in production */}
            <button 
                onClick={debugCart}
                className="fixed top-4 right-4 bg-blue-500 text-white px-3 py-1 rounded text-sm z-50"
            >
                Debug Cart
            </button>

            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-charcoal mb-2">Your Shopping Cart</h1>
                    <p className="text-charcoal/60">
                        {calculateTotalItems()} {calculateTotalItems() === 1 ? 'item' : 'items'} in your cart
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Cart Items */}
                    <div className="lg:col-span-2 space-y-4">
                        {cartItems.map((item, index) => {
                            const currentPrice = item.productDiscountPrice || item.productPrice;
                            const originalPrice = item.productDiscountPrice ? item.productPrice : null;
                            const discount = originalPrice ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100) : 0;

                            return (
                                <div key={item.productVariantKey || index} className="bg-offwhite rounded-lg p-4 shadow-sm border border-taupe/30">
                                    <div className="flex flex-col sm:flex-row gap-4">
                                        {/* Product Image */}
                                        <div className="flex-shrink-0">
                                            <img
                                                src={item.productImage}
                                                alt={item.productName}
                                                className="w-20 h-20 object-cover rounded border border-taupe/30"
                                                onError={(e) => {
                                                    e.target.src = '/placeholder-image.jpg';
                                                }}
                                            />
                                        </div>

                                        {/* Product Details */}
                                        <div className="flex-grow">
                                            <div className="flex justify-between items-start mb-2">
                                                <h3 className="font-semibold text-charcoal text-lg">
                                                    {item.productName}
                                                </h3>
                                                <button
                                                    onClick={() => removeFromCart(index)}
                                                    className="text-redburnt hover:text-red-700 transition-colors p-1"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>

                                            {/* Variant Details */}
                                            <div className="space-y-2 mb-3">
                                                <div className="flex items-center space-x-4 text-sm text-charcoal/70">
                                                    <span>Color: <span className="font-medium capitalize">{item.productColor}</span></span>
                                                    <span>Size: <span className="font-medium">{item.productSize}</span></span>
                                                </div>
                                                
                                                {/* Sole Type Selection */}
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-sm text-charcoal/70">Sole Type:</span>
                                                    <select
                                                        value={item.productSoleType || 'Leather'}
                                                        onChange={(e) => updateSoleType(index, e.target.value)}
                                                        className="text-sm border border-taupe/50 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                                                    >
                                                        {soleTypes.map((soleType, soleIndex) => (
                                                            <option key={soleIndex} value={soleType.name}>
                                                                {soleType.name} {soleType.priceAdjustment !== 0 ? 
                                                                    `(${soleType.priceAdjustment > 0 ? '+' : ''}${soleType.priceAdjustment} PKR)` : 
                                                                    ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {item.soleAdjustment !== 0 && (
                                                        <span className={`text-xs ${item.soleAdjustment < 0 ? 'text-green-600' : 'text-redburnt'}`}>
                                                            {item.soleAdjustment > 0 ? '+' : ''}{item.soleAdjustment} PKR
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Price and Quantity */}
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center space-x-3">
                                                    {/* Quantity Controls */}
                                                    <div className="flex items-center border border-taupe/50 rounded">
                                                        <button
                                                            onClick={() => updateQuantity(index, item.productQuantity - 1)}
                                                            disabled={item.productQuantity <= 1}
                                                            className="px-3 py-1 text-charcoal hover:bg-taupe/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            <Minus size={16} />
                                                        </button>
                                                        <span className="px-3 py-1 text-charcoal font-semibold min-w-8 text-center">
                                                            {item.productQuantity}
                                                        </span>
                                                        <button
                                                            onClick={() => updateQuantity(index, item.productQuantity + 1)}
                                                            className="px-3 py-1 text-charcoal hover:bg-taupe/20"
                                                        >
                                                            <Plus size={16} />
                                                        </button>
                                                    </div>

                                                    {/* Price */}
                                                    <div className="flex items-center space-x-2">
                                                        <span className="font-bold text-primary text-lg">
                                                            PKR {(currentPrice * item.productQuantity).toLocaleString()}
                                                        </span>
                                                        {originalPrice && (
                                                            <span className="text-sm text-charcoal/50 line-through">
                                                                PKR {(originalPrice * item.productQuantity).toLocaleString()}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {discount > 0 && (
                                                    <span className="bg-redburnt/10 text-redburnt px-2 py-1 rounded-full text-xs font-semibold">
                                                        Save {discount}%
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-offwhite rounded-lg p-6 shadow-sm border border-taupe/30 sticky top-6">
                            <h2 className="text-xl font-bold text-charcoal mb-4">Order Summary</h2>
                            
                            <div className="space-y-3 mb-6">
                                <div className="flex justify-between text-charcoal/70">
                                    <span>Subtotal ({calculateTotalItems()} items)</span>
                                    <span>PKR {calculateTotal().toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between text-charcoal/70">
                                    <span>Shipping</span>
                                    <span className="text-green-600">Free</span>
                                </div>
                                <div className="border-t border-taupe/30 pt-3">
                                    <div className="flex justify-between text-lg font-bold text-charcoal">
                                        <span>Total</span>
                                        <span>PKR {calculateTotal().toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={proceedToCheckout}
                                className="w-full bg-primary text-offwhite py-3 rounded-lg hover:bg-primary-dark transition-colors font-semibold flex items-center justify-center space-x-2"
                            >
                                <span>Proceed to Checkout</span>
                                <ArrowRight size={18} />
                            </button>

                            <button
                                onClick={continueShopping}
                                className="w-full border border-primary text-primary py-3 rounded-lg hover:bg-primary/10 transition-colors font-semibold mt-3"
                            >
                                Continue Shopping
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Cart;